/**
 * System default branding configuration.
 * Used as fallback when API is unavailable or tenant has no custom branding.
 * Purple primary color is the ArgusAI brand identity.
 */

export interface BrandingColors {
  primary: string;
  primaryForeground: string;
  background: string;
  card: string;
  text: string;
  popover: string;
  sidebarBackground: string;
  sidebarText: string;
  sidebarActive: string;
  sidebarActiveText: string;
  sidebarHover: string;
}

export interface TenantBranding {
  useDefaultTheme: boolean;
  displayMode: 'full' | 'both' | 'icon-name';
  portalTitle: string;
  logoUrl: string | null;
  logoIconUrl: string | null;
  faviconUrl: string | null;
  light: BrandingColors;
  dark: BrandingColors;
  // ── Wave 1c W1c-05 — fields surfaced by the effective-branding shape. ──
  // Optional so existing callers (unit tests, fallback constructors) keep
  // type-checking; the API always returns them populated.
  loginBackgroundUrl?: string | null;
  loginWelcomeText?: string | null;
  loginSubtitle?: string | null;
  footerText?: string | null;
  effectiveVersion?: number;
  lockedFields?: string[];
  source?: 'tenant' | 'platform' | 'system_default';
}

export const SYSTEM_DEFAULT_BRANDING: TenantBranding = {
  useDefaultTheme: true,  // Use CSS @theme + .dark OKLCH values (index.css) as defaults
  displayMode: 'icon-name',
  portalTitle: 'ArgusAI',
  logoUrl: '/ArgusLogo_500px-Wordmark-TM.png',
  logoIconUrl: '/ArgusLogo_500px-Logo.png',
  faviconUrl: '/favicon.png',
  // Hex values aligned with index.css OKLCH values (used when custom branding overrides CSS)
  light: {
    primary: '#8B5CF6',           // oklch(0.585 0.233 292) — ArgusAI Purple
    primaryForeground: '#ffffff',
    background: '#fafafa',        // oklch(0.985 0 0) — near-white
    card: '#ffffff',              // oklch(1.0 0 0) — pure white
    text: '#1a1a1d',              // oklch(0.145 0.005 285) — near-black
    popover: '#ffffff',
    sidebarBackground: '#f5f5f5', // oklch(0.975 0 0) — slightly off-white
    sidebarText: '#1a1a1d',       // matches foreground
    sidebarActive: '#8B5CF6',     // purple active
    sidebarActiveText: '#ffffff',
    sidebarHover: '#efe8fd',      // oklch(0.955 0.025 292) — soft purple tint
  },
  dark: {
    primary: '#9d70fa',           // oklch(0.65 0.20 292) — brighter purple for dark bg
    primaryForeground: '#ffffff',
    background: '#222222',        // oklch(0.145 0 0) — pure neutral dark
    card: '#2e2e32',              // oklch(0.195 0.003 285) — subtle purple DNA
    text: '#ededed',              // oklch(0.93 0 0) — clean off-white
    popover: '#333337',           // oklch(0.21 0.003 285) — elevated from card
    sidebarBackground: '#1c1c1c', // oklch(0.12 0 0) — darker for depth
    sidebarText: '#b0b2ba',       // oklch(0.72 0.005 260) — readable sidebar text
    sidebarActive: '#8B5CF6',
    sidebarActiveText: '#ffffff',
    sidebarHover: '#342b42',      // oklch(0.21 0.02 292) — purple-tinted hover
  },
  // ── Wave 1c W1c-05 — pre-populated defaults for new fields. ──
  loginBackgroundUrl: null,
  loginWelcomeText: null,
  loginSubtitle: null,
  footerText: null,
  effectiveVersion: 0,
  lockedFields: [],
  source: 'system_default',
};
